import { qs, qsa, remove } from '@fluorescent/dom';
import anime from 'animejs/lib/anime.es.js';
import { on, emit } from '@/neon/events';
import { syncTabIndexesOnFlickity, backgroundVideoHandler, prefersReducedMotion } from '@/lib/a11y';

import { section } from '@/neon';
import autoPlay from '@/lib/auto-play';
import navigationDots from '@/lib/navigation-dots';

const sel = {
  slider: '[data-slider]',
  video: '.slideshow__video'
};

section('slideshow', {
  slideshow: null,
  events: [],

  onLoad() {
    const reduceMotion = prefersReducedMotion();
    const slider = qs(sel.slider, this.container);
    const { autoplay, parallax } = this.container.dataset;
    const videos = qsa(sel.video, this.container);
    this.videoHandlers = [];

    if (!reduceMotion) {
      autoPlay(videos);
    }

    if (videos.length) {
      videos.forEach(video => {
        this.videoHandlers.push(backgroundVideoHandler(video.parentNode));
      });
    }

    remove(slider, 'is-hidden');
    // trigger redraw for transition
    slider.offsetHeight;
    this.slideshow = null;

    import('flickity').then(({ default: Flickity }) => {
      this.slideshow = new Flickity(slider, {
        adaptiveHeight: true,
        autoPlay: !reduceMotion && Number(autoplay),
        draggable: true,
        prevNextButtons: false,
        wrapAround: true,
        pageDots: false,
        on: {
          ready: () => {
            const first = qs('[data-slide]', this.container);
            var slideshow = Flickity.data(slider); // This is a pain but we can't access the instance within here

            syncTabIndexesOnFlickity(slideshow);

            anime({
              targets: qsa('.animate', first),
              easing: 'easeOutQuart',
              translateY: [
                { value: 40, duration: 0 },
                { value: 0, duration: 500 },
              ],
              opacity: [
                { value: 0, duration: 0 },
                { value: 1, duration: 500 },
              ],
              delay: anime.stagger(150, { start: 800 }),
            });
          },
        },
      });

      this.dotNavigation = navigationDots(this.container, this.slideshow);

      this.slideshow.on('change', index => {
        const activeSlide = this.slideshow.cells[index].element;
        this.dotNavigation && this.dotNavigation.update(index);
        syncTabIndexesOnFlickity(this.slideshow);

        anime({
          targets: qsa('.animate', activeSlide),
          easing: 'easeOutQuart',
          translateY: [
            { value: 40, duration: 0 },
            { value: 0, duration: 500 },
          ],
          opacity: [
            { value: 0, duration: 0 },
            { value: 1, duration: 500 },
          ],
          delay: anime.stagger(150, { start: 200 }),
        });
      });

      emit('slideshow:initialized');

    });

    if (parallax == 'true') {
      import('simple-parallax-js').then(({ default: simpleParallax }) => {
        const images = this.container.querySelectorAll('.image__img');
        this.parallax = new simpleParallax(images, {
          customWrapper: '.slideshow__parallax-wrapper',
          scale: 1.5,
        });
      });
    }
  },

  _handleBlockSelect(slideIndex) {
    this.slideshow.select(slideIndex);
    this._pause();
  },

  _pause() {
    this.slideshow && this.slideshow.pausePlayer();
  },

  _unpause() {
    this.slideshow && this.slideshow.unpausePlayer();
  },

  onBlockSelect({ target }) {
    if (this.slideshow) {
      this._handleBlockSelect(target.dataset.index);
    } else {
      // Listen for initalization if slideshow does not exist
      this.events.push(
        on('slideshow:initialized', () => {
          this._handleBlockSelect(target.dataset.index);
        })
      );
    }
  },

  onBlockDeselect() {
    if (this.slideshow) {
      this._unpause();
    } else {
      // Listen for initalization if slideshow does not exist
      this.events.push(
        on('slideshow:initialized', () => {
          this._unpause();
        })
      );
    }
    this.parallax && this.parallax.destroy();

  },

  onUnload() {
    this.slideshow && this.slideshow.destroy();
    this.events.forEach(unsubscribe => unsubscribe());
    this.videoHandlers.forEach(handler => handler());
    this.dotNavigation && this.dotNavigation.unload();
  },
});
